/**
 * Post-processing — pipeline stages S13–S16 and S19–S22.
 *
 * Spec: docs/spec/00-MASTER-SPEC.md §3.11 (R-298 … R-307) and §1 (pipeline order).
 * Source: latex2html-french-trad/src/lib/latexConverter.ts — `processParagraphs`
 * plus the inline one-liners in `convertLatexToHtml`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * STAGE ORDER (the coordinator in index.ts must call these in exactly this order;
 * every ordering here is load-bearing and several defects depend on it):
 *
 *   S13  convertLineBreaks(html)  R-298
 *   S14  stripEmptyBraces(html)  R-299
 *   S15  processParagraphs(html)  R-300…R-304
 *   S16  cleanupEmptyParagraphs(html)  R-305
 *   S17  buildTableOfContents(...)  ── toc.ts
 *   S18  insertTableOfContents(...) ── toc.ts
 *   S19  suppressPageBreakBeforeHiddenToc(html)  R-306
 *   S20  stripEmptyBraces(html)      (called AGAIN)       R-306
 *   S21  dedupeConsecutivePageBreaks(html)  R-306
 *   S22  collapseBlankLinesAndTrim(html)  R-307
 *
 * S16 running BEFORE S17 is observable: `buildTableOfContents` deletes the
 * `<span class="manual-toc-entry">` markers, and any `<p>` that held nothing but a
 * marker is left behind as a literal `<p></p>` in the final document — S16 has
 * already run and never gets a second chance. Confirmed against the oracle; see
 * tests/golden/structure/06-addcontentsline-levelskip.*.html.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * BUG-FOR-BUG (CLAUDE.md §3). Reproduced deliberately, pinned by tests in
 * paragraphs.test.ts:
 *   G-8   `page-break` is absent from BOTH skip lists, so a mid-paragraph \newpage
 *         emits `<p>…<div class="page-break">…</div></p>` — a div inside a p.
 *   G-9   `bigskip`/`medskip` divs emitted by \arbsq, \Tital, \Ntablet, \glad,
 *         \KAnote land inside a `<p>` for the same reason.
 *   G-10  Nested quotes produce `<blockquote>` inside `<p>` inside `<blockquote>`.
 *   G-13  The hidden-TOC divider rewrite only matches a divider BEFORE the nav, so
 *         \afficherSommaire's trailing \newpage divider survives visible.
 *
 * NOT PORTED — `removeParBreakInsideBlocks`. It exists only in the source repo's
 * scratch file `test_quote_debug.js:4`, which nothing imports; it is not reachable
 * from `latexConverter.ts` at any stage (spec §2.2 claim 13, verdict CONFIRMED (R)).
 * It is also internally broken there — it advances `i += 17` for a 16-character
 * marker. Porting it would invent behaviour the production app has never had.
 *
 * Pure: no I/O, no DOM, no module-level mutable state. The source's `console.log`
 * debug calls are omitted — they never touch the returned
 * `{ html, css }`.
 *
 * VERIFIED AGAINST THE ORIGINAL, not against a reading of it:
 *   • ~24,000 generated `processParagraphs` inputs, byte-identical.
 *   • 266 generated inputs for the one-liners, compared against the real source
 *     lines sliced out of latexConverter.ts and evaluated — no retyping.
 *   • Corpus replay: for all 74 fixtures × 2 modes, the original's html was
 *     captured at every stage boundary and each stage below re-run on it —
 *     1,628 stage comparisons, 0 mismatches.
 */

import { PAGE_BREAK_HTML } from './commands-text'

/**
 * Escape a literal string for use inside a `RegExp`.
 *
 * Exists so S19 can build its pattern from `PAGE_BREAK_HTML` instead of repeating the
 * divider markup. The separator contains `(`, `)`, `.`, `/`, `*` and `?` in its SVG path
 * data, every one of which is a regex metacharacter.
 */
function escapeRegExp(literal: string): string {
  return literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * S13 (R-298) — every surviving `\\` becomes a line break.
 *
 * Deliberately deferred until after `processEnvironments` so that `tabular` and
 * `longtable` can still split rows on `\\` (spec §1 consequence 9).
 */
export function convertLineBreaks(html: string): string {
  // R-298: /\\\\/g → '<br>'
  return html.replace(/\\\\/g, '<br>')
}

/**
 * S14 (R-299) and again at S20 (R-306) — drop empty brace pairs
 * left behind by zero-argument commands such as `\afficherTitre{}`.
 *
 * The source writes this as `/{}/g` at and `/\{\}/g` at. Under
 * Annex B web-compatibility semantics a `{` that cannot start a quantifier is an
 * ordinary pattern character, so the two are the same regex; one function serves
 * both call sites. **The coordinator must still call it twice** — stages S15–S19
 * run in between and S17 can expose new `{}` pairs.
 */
export function stripEmptyBraces(html: string): string {
  // R-299 / R-306
  return html.replace(/\{\}/g, '')
}

/**
 * S15 (R-300 … R-304) — wrap loose text in `<p>`.
 *
 * Also used as `processInner` by `processEnvironments`, so inner blocks
 * are paragraph-wrapped before the outer global pass ever sees them.
 */
export function processParagraphs(html: string): string {
  // R-300 — separate a block closing tag from text glued to it, so the
  // splitter below sees them as two blocks. `([^<\n])` is a single character that
  // is neither `<` nor a newline, and it is re-emitted after the inserted break.
  // Note `</span>`, `</em>`, `</strong>`, `</a>` are NOT in the list.
  const separated = html.replace(
    /(<\/(?:h[1-6]|div|p|blockquote|ul|ol|dl|table|figure|pre|nav)>)([^<\n])/g,
    '$1\n\n$2',
  )

  // R-301 — split on the \par marker first, then on blank lines.
  const parBlocks = separated.split('<!--PAR_BREAK-->')

  return parBlocks
    .map((block) => {
      const lineBlocks = block.split(/\n\s*\n/)

      return lineBlocks
        .map((lineBlock) => {
          const trimmed = lineBlock.trim()
          if (!trimmed) return ''

          // R-302 — "already inside a block element" skip test.
          // VERBATIM LIST. These ten prefixes are returned unwrapped; everything
          // else falls through. The test is a bare `startsWith` on the tag prefix,
          // so `<header>` counts as `<h`, and `<pre>`/`<p>` both match `<p`.
          // `page-break` is NOT here and `<div` is handled separately below (G-8).
          if (
            trimmed.startsWith('<h') ||
            trimmed.startsWith('<ul') ||
            trimmed.startsWith('<ol') ||
            trimmed.startsWith('<dl') ||
            trimmed.startsWith('<table') ||
            trimmed.startsWith('<blockquote') ||
            trimmed.startsWith('<pre') ||
            trimmed.startsWith('<figure') ||
            trimmed.startsWith('<nav') ||
            trimmed.startsWith('<p')
          ) {
            return trimmed
          }

          // R-303 — `<div` blocks.
          if (trimmed.startsWith('<div')) {
            // Only these five spacer classes, and only with the class attribute
            // closing immediately (`">`), are split from their trailing content.
            // `<div class="page-break">` does not match — hence G-8.
            if (/^<div class="(?:smallskip|medskip|bigskip|vfill|arbsq)">/.test(trimmed)) {
              const closingDivIndex = trimmed.indexOf('</div>')
              if (closingDivIndex !== -1) {
                const spacer = trimmed.substring(0, closingDivIndex + 6)
                const rest = trimmed.substring(closingDivIndex + 6).trim()
                if (rest) {
                  // A leading margin-note span is emitted OUTSIDE the paragraph.
                  // This is what keeps a verse number in the margin. Non-greedy
                  // `.*?` with the `s` flag: a nested `</span>` closes it early.
                  const marginMatch = rest.match(
                    /^(<span class="(?:nverset|marginpar|npar|nhidden)(?: [^"]+)?">.*?<\/span>)\s*(.*)/s,
                  )

                  if (marginMatch) {
                    // Both groups always participate when the match succeeds;
                    // the `?? ''` only satisfies noUncheckedIndexedAccess.
                    const span = marginMatch[1] ?? ''
                    const text = (marginMatch[2] ?? '').trim()
                    if (text) {
                      if (/^<(?:blockquote|div|p|ul|ol|dl|table|figure|pre|nav|h[1-6])/.test(text)) {
                        return `${spacer}\n${span}\n${text}`
                      }
                      return `${spacer}\n${span}\n<p>${text}</p>`
                    }
                    return `${spacer}\n${span}`
                  }

                  if (/^<(?:blockquote|div|p|ul|ol|dl|table|figure|pre|nav|h[1-6])/.test(rest)) {
                    return `${spacer}\n${rest}`
                  }

                  return `${spacer}\n<p>${rest}</p>`
                }
                return spacer
              }
              // No `</div>`: falls through to the generic div return below.
            }
            // Any other `<div` block — including `<div class="page-break">` —
            // is returned unwrapped.
            return trimmed
          }

          // R-304 — a block that starts directly with a margin-note
          // span (\Npar expands without a preceding \smallskip). The outer guard is
          // a prefix test with no closing quote, so `class="marginparX"` passes it
          // and then fails the inner regex, falling through to the `<p>` wrap.
          if (/^<span class="(?:marginpar|nverset|npar|nhidden)/.test(trimmed)) {
            const marginMatch = trimmed.match(
              /^(<span class="(?:nverset|marginpar|npar|nhidden)(?: [^"]+)?">.*?<\/span>)\s*(.*)/s,
            )
            if (marginMatch) {
              const span = marginMatch[1] ?? ''
              const text = (marginMatch[2] ?? '').trim()
              if (text) {
                if (/^<(?:blockquote|div|p|ul|ol|dl|table|figure|pre|nav|h[1-6])/.test(text)) {
                  return `${span}\n${text}`
                }
                return `${span}\n<p>${text}</p>`
              }
              return span
            }
          }

          // Bare text.
          if (trimmed) {
            return `<p>${trimmed}</p>`
          }
          return ''
        })
        .filter((b) => b)
        .join('\n')
      // NOTE the asymmetry: the INNER map filters empty results
      // the OUTER map does not. An empty \par block therefore contributes an empty
      // string to the outer join and leaves a stray '\n' in the output.
    })
    .join('\n')
}

/**
 * `processInner` — the alias `processEnvironments` uses to wrap the
 * contents of `quote`, `quotation`, `verse`, `center`, `flushleft` and
 * `flushright` before the outer S15 pass runs. Identical to `processParagraphs`;
 * named separately so the environments module cites rather than.
 */
export const processInner = processParagraphs

/**
 * S16 (R-305) — three cleanups, in this order.
 *
 * Runs BEFORE the TOC stage, so empty paragraphs created later — when
 * `buildTableOfContents` deletes a `manual-toc-entry` span that was alone in its
 * paragraph — are NOT cleaned up and reach the reader as `<p></p>`.
 */
export function cleanupEmptyParagraphs(html: string): string {
  // R-305
  let out = html.replace(/<p>\s*<\/p>/g, '')
  out = out.replace(/<\/p>\s*<\/p>/g, '</p>')
  out = out.replace(/<p>\s*<p>/g, '<p>')
  return out
}

/**
 * S19 (R-306) — when the TOC came out hidden, mark the divider that
 * immediately precedes it hidden too.
 *
 * DEFECT G-13, preserved: the pattern only matches a divider BEFORE the nav.
 * `\afficherSommaire` expands to `\tableofcontents\thispagestyle{empty}\newpage`
 * (pre:111), so its divider comes AFTER — and stays visible in an empty document.
 * Note also that the whitespace matched by `\s*` is dropped, not preserved.
 */
export function suppressPageBreakBeforeHiddenToc(html: string): string {
  // R-306. The pattern is BUILT FROM `PAGE_BREAK_HTML`, not hard-coded.
  // It used to repeat the divider markup literally, in two places — which meant that
  // changing the separator in commands-text.ts would silently stop this rule matching,
  // with no test failing and no error anywhere. Deriving it removes that class of drift.
  const opener = '<div class="page-break">'
  const hiddenOpener = '<div class="page-break toc-hidden">'
  const nav = '<nav class="table-of-contents toc-hidden">'
  const pattern = new RegExp(
    `${escapeRegExp(PAGE_BREAK_HTML)}\\s*${escapeRegExp(nav)}`,
    'g',
  )
  return html.replace(
    pattern,
    PAGE_BREAK_HTML.replace(opener, hiddenOpener) + nav,
  )
}

/**
 * S21 (R-306) — collapse runs of consecutive page-break dividers to
 * the first one.
 *
 * Both patterns require the literal `<div class="page-break">`, so a divider that
 * `buildTableOfContents` has given an id (`<div id="…" class="page-break">`) is
 * exempt and survives next to its neighbour. The `while` is needed because the
 * global replace consumes pairs and can leave a new adjacency behind.
 */
export function dedupeConsecutivePageBreaks(html: string): string {
  // R-306
  let out = html
  while (/<div class="page-break">.*?<\/div>\s*<div class="page-break">/s.test(out)) {
    out = out.replace(
      /(<div class="page-break">.*?<\/div>)\s*(<div class="page-break">.*?<\/div>)/gs,
      '$1',
    )
  }
  return out
}

/**
 * S22 (R-307) — the final tidy. Three or more newlines collapse to
 * two, then the whole document is trimmed. The trim is why golden `.html` files
 * carry no trailing newline.
 */
export function collapseBlankLinesAndTrim(html: string): string {
  // R-307
  return html.replace(/\n{3,}/g, '\n\n').trim()
}
